/**
 * Share adapters (ui/urlState.ts, `share`) for page state kept as dictionary indexes.
 *
 * Claim and Touch detail keep their dropdowns as indexes into the dictionaries the API
 * builds for each caller's rows. Those differ by caller — a scoped reader's facility 12
 * is not a full-access reader's — so a link carries the VALUES, and the receiver looks
 * them up in their own dictionaries. A value they do not have (a person or a facility
 * outside their access) is dropped, not guessed.
 */
import { indexOfValue } from '@/data/blankValue'
import type { ShareAdapter } from '@/ui/urlState'

type Dicts = Record<string, readonly string[] | undefined>

/** A `{ key: index }` record of dropdowns (as strings, '' for none) ⇄ `{ key: value }`. */
export function pickAdapter(dicts: Dicts | null): ShareAdapter {
  return {
    out: (raw) => {
      if (!dicts) return null
      let picked: Record<string, string>
      try {
        picked = JSON.parse(raw) as Record<string, string>
      } catch {
        return null
      }
      const out: Record<string, string> = {}
      for (const [k, i] of Object.entries(picked)) {
        const v = i === '' ? undefined : dicts[k]?.[Number(i)]
        if (v !== undefined) out[k] = v
      }
      return Object.keys(out).length ? JSON.stringify(out) : null
    },
    in: (raw) => {
      if (!dicts) return undefined
      let values: Record<string, string>
      try {
        values = JSON.parse(raw) as Record<string, string>
      } catch {
        return null
      }
      // Every dropdown, '' where the link has nothing for it: the page reads them all.
      const out: Record<string, string> = Object.fromEntries(Object.keys(dicts).map((k) => [k, '']))
      let any = false
      for (const [k, v] of Object.entries(values)) {
        const d = dicts[k]
        const i = d && typeof v === 'string' ? indexOfValue(d, v) : -1
        if (i >= 0) {
          out[k] = String(i)
          any = true
        }
      }
      return any ? JSON.stringify(out) : null
    },
  }
}

/** One index (as JSON, `12`) ⇄ its value. */
export function indexAdapter(dict: readonly string[] | null | undefined, ready: boolean): ShareAdapter {
  return {
    out: (raw) => {
      const v = dict?.[Number(raw)]
      return v === undefined ? null : v
    },
    in: (raw) => {
      if (!ready) return undefined
      const i = dict ? indexOfValue(dict, raw) : -1
      return i >= 0 ? JSON.stringify(i) : null
    },
  }
}
